import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { getRandomExamples } from '../../config/aiExamplePrompts';
import './AIGeneratePanel.css';

/**
 * Props for AIGeneratePanel component
 */
export interface AIGeneratePanelProps {
  /** Callback when generate is requested */
  onGenerate: (prompt: string) => void;
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Current loading message */
  loadingMessage?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Error message to display */
  errorMessage?: string;
  /** Callback when panel is closed */
  onClose: () => void;
  /** Optional callback for sign in action (shown when auth error) */
  onSignIn?: () => void;
  /** Whether the user has an existing composition (for mode detection) */
  hasExistingComposition?: boolean;
  /** Callback to reset/clear the composition */
  onReset?: () => void;
  /** Current credit balance (undefined = not authenticated / not loaded) */
  credits?: number;
  /** Callback to open the buy credits modal */
  onBuyCredits?: () => void;
}

/**
 * AIGeneratePanel Component
 * 
 * Full-screen panel for AI music generation on mobile.
 * Provides prompt input and generate button, with loading state.
 */
export const AIGeneratePanel: React.FC<AIGeneratePanelProps> = ({
  onGenerate,
  isGenerating,
  loadingMessage = 'Generating...',
  progress = 0,
  errorMessage,
  onClose,
  onSignIn,
  hasExistingComposition = false,
  onReset,
  credits,
  onBuyCredits,
}) => {
  const [prompt, setPrompt] = useState('');
  const [showScrollGradient, setShowScrollGradient] = useState(true);
  const [showButtonAnimation, setShowButtonAnimation] = useState(false);
  const [showProTips, setShowProTips] = useState(false);
  const examplesRef = React.useRef<HTMLDivElement>(null);
  const previousPromptEmpty = React.useRef(true);

  // Pick 4 random examples each time the panel mounts
  const examples = useMemo(() => getRandomExamples(4), []);

  /**
   * Check if examples container is scrollable and update gradient visibility
   */
  const checkScrollGradient = useCallback(() => {
    const container = examplesRef.current;
    if (!container) return;

    const isScrollable = container.scrollWidth > container.clientWidth;
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
    
    setShowScrollGradient(isScrollable && !isAtEnd);
  }, []);

  /**
   * Set up scroll listener and initial check
   */
  useEffect(() => {
    const container = examplesRef.current;
    if (!container) return;

    let rafId: number | null = null;

    // Throttled scroll handler using requestAnimationFrame
    const handleScroll = () => {
      if (rafId !== null) return;
      
      rafId = requestAnimationFrame(() => {
        checkScrollGradient();
        rafId = null;
      });
    };

    // Initial check
    checkScrollGradient();

    // Add scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Add resize listener in case window size changes
    window.addEventListener('resize', checkScrollGradient);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollGradient);
    };
  }, [checkScrollGradient]);

  /**
   * Handle generate button click
   */
  const handleGenerate = useCallback(() => {
    if (!prompt.trim() || isGenerating) return;
    onGenerate(prompt.trim());
  }, [prompt, isGenerating, onGenerate]);

  /**
   * Handle prompt change
   */
  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  }, []);

  /**
   * Trigger celebration animation when button becomes enabled
   */
  useEffect(() => {
    const isPromptEmpty = !prompt.trim();
    
    // Trigger animation when transitioning from empty to non-empty
    if (previousPromptEmpty.current && !isPromptEmpty) {
      setShowButtonAnimation(true);
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setShowButtonAnimation(false);
      }, 600); // Match animation duration
      
      previousPromptEmpty.current = isPromptEmpty;
      return () => clearTimeout(timer);
    }
    
    previousPromptEmpty.current = isPromptEmpty;
    return undefined; // Explicit return for TypeScript
  }, [prompt]);

  /**
   * Clear prompt after successful generation
   */
  useEffect(() => {
    if (!isGenerating && progress === 100) {
      setPrompt('');
    }
  }, [isGenerating, progress]);

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="ai-generate-panel-backdrop" 
        onClick={!isGenerating ? onClose : undefined}
        aria-hidden="true"
      />
      
      {/* Panel container */}
      <div 
        className="ai-generate-panel" 
        role="dialog" 
        aria-label="AI music generation"
        aria-modal="true"
      >
        {/* Header with close button */}
        <div className="ai-generate-panel-header">
          <h2 className="ai-generate-panel-title">
            <i className="fa-solid fa-wand-magic-sparkles"></i>
            AI Generate
          </h2>
          <div className="ai-generate-panel-header-right">
            {credits !== undefined && (
              <div className="ai-generate-credit-badge">
                <span className="ai-generate-credit-count" aria-live="polite" aria-atomic="true">
                  <i className="fa-solid fa-coins" aria-hidden="true"></i>
                  {credits}
                </span>
                {onBuyCredits && (
                  <button
                    className="ai-generate-buy-btn"
                    onClick={onBuyCredits}
                    aria-label="Buy more credits"
                    type="button"
                  >
                    + Buy
                  </button>
                )}
              </div>
            )}
            {!isGenerating && (
              <button
                className="ai-generate-panel-close"
                onClick={onClose}
                aria-label="Close AI generation"
                title="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="ai-generate-panel-content">
          {isGenerating ? (
            // Loading State
            <div className="ai-generate-loading">
              {/* Loading Message */}
              <div className="ai-generate-loading-message">
                <span className="ai-generate-loading-icon">🎮</span>
                <span className="ai-generate-loading-text">{loadingMessage}</span>
              </div>

              {/* Progress Bar */}
              <div className="ai-generate-progress-container">
                <div 
                  className="ai-generate-progress-bar"
                  style={{ width: `${progress}%` }}
                >
                  <div className="ai-generate-progress-scanline" />
                </div>
                <div className="ai-generate-progress-text">
                  {Math.round(progress)}%
                </div>
              </div>

              {/* Loading Tip */}
              <div className="ai-generate-loading-tip">
                ⚡ PLEASE WAIT - DO NOT CLOSE ⚡
              </div>
            </div>
          ) : (
            // Input State
            <>
              {/* Mode Indicator */}
              {hasExistingComposition && (
                <div className="ai-generate-mode-notice">
                  <i className="fa-solid fa-info-circle"></i>
                  <div className="ai-generate-mode-notice-content">
                    <span>
                      You have an existing composition. AI will edit and enhance your current work.
                    </span>
                    {onReset && (
                      <button
                        className="ai-generate-mode-notice-button"
                        onClick={onReset}
                        type="button"
                      >
                        <i className="fa-solid fa-rotate-left"></i>
                        Start Fresh
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="ai-generate-instructions">
                <p>Describe the type of 8-bit music you want to create:</p>
              </div>

              {/* Prompt Input */}
              <textarea
                className="ai-generate-prompt"
                rows={6}
                value={prompt}
                onChange={handlePromptChange}
                placeholder="e.g., 'upbeat battle theme' or 'peaceful village music'..."
                aria-label="Music generation prompt"
              />

              {/* Error Message */}
              {errorMessage && (
                <div className="ai-generate-error">
                  <div className="ai-generate-error-content">
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <span>{errorMessage}</span>
                  </div>
                  {onSignIn && errorMessage.toLowerCase().includes('sign in') && (
                    <button
                      className="ai-generate-error-button"
                      onClick={onSignIn}
                      type="button"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              )}

              {/* Examples */}
              <div 
                ref={examplesRef}
                className={`ai-generate-examples ${showScrollGradient ? 'ai-generate-examples--scrollable' : ''}`}
              >
                <div className="ai-generate-examples-title">Examples:</div>
                {examples.map((ex) => (
                  <button
                    key={ex.label}
                    className="ai-generate-example-chip"
                    onClick={() => setPrompt(ex.prompt)}
                    type="button"
                  >
                    {ex.emoji && <span className="ai-generate-example-emoji">{ex.emoji}</span>}
                    {ex.label}
                  </button>
                ))}
              </div>

              {/* Quick Tips Section - Collapsible */}
              <div className="ai-generate-tips">
                <button 
                  className="ai-generate-tips-toggle"
                  onClick={() => setShowProTips(!showProTips)}
                  type="button"
                >
                  <i className="fa-solid fa-lightbulb"></i>
                  Pro Tips
                  <i className={`fa-solid fa-chevron-${showProTips ? 'up' : 'down'} ai-generate-tips-chevron`}></i>
                </button>
                {showProTips && (
                  <div className="ai-generate-tips-grid">
                    <div className="ai-generate-tip-card">
                      <div className="ai-generate-tip-icon">🎵</div>
                      <div className="ai-generate-tip-text">
                        <strong>Be specific</strong> about mood and tempo
                      </div>
                    </div>
                    <div className="ai-generate-tip-card">
                      <div className="ai-generate-tip-icon">🎹</div>
                      <div className="ai-generate-tip-text">
                        <strong>Mention instruments</strong> like "square lead" or "triangle bass"
                      </div>
                    </div>
                    <div className="ai-generate-tip-card">
                      <div className="ai-generate-tip-icon">⚡</div>
                      <div className="ai-generate-tip-text">
                        <strong>Set length & tempo</strong> like "16 steps at 140 BPM"
                      </div>
                    </div>
                    <div className="ai-generate-tip-card">
                      <div className="ai-generate-tip-icon">🎼</div>
                      <div className="ai-generate-tip-text">
                        <strong>Describe the feel</strong> like "energetic", "mysterious", or "calm"
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button
                className={`ai-generate-button ${showButtonAnimation ? 'ai-generate-button-celebrate' : ''}`}
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                aria-label="Generate music from prompt"
              >
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                Generate Music
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
